import { c as _c } from "react/compiler-runtime";
import type { FragmentOfShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { Image } from "@repo/components/atoms/image";
import type { Usable } from "react";
import { Suspense } from "react";
import { HiOutlineMinus, HiOutlinePlus } from "react-icons/hi2";
import type { ResolvedIntlConfig } from "react-intl";
import { Button } from "@repo/components/atoms/shadcn/button";
import { Link } from "@repo/components/atoms/link";
import { Price } from "@repo/components/atoms/price";
import { DeleteItemButton } from "#/components/molecules/cart/delete-item-button";
import { EditItemQuantityButton } from "#/components/molecules/cart/edit-item-quantity-button";
import { DEFAULT_OPTION } from "@repo/constants";
import { readFragmentShopifyStorefront } from "@repo/graphql-shopify-storefront";
import { useGetIntl } from "@repo/i18n/intl/client";
import { cn } from "@repo/lib";
import { transformShopifyImageToNextImage } from "@repo/shopify/utilities/next";
import { BaseCartLine_Product_Fragment, BaseCartLine_BaseCartLine_Fragment } from "./graphql";
import { Cart_Fragment, Image_Fragment, SellingPlanGroup_Fragment } from "#/graphql/shopify-storefront/fragments";

// FIX: Extracted component outside of render
function CartLineProductImage(t0) {
  const $ = _c(3);
  const {
    image,
    title
  } = t0;
  if (!image) {
    return null;
  }
  const t1 = image.alt ?? title;
  let t2;
  if ($[0] !== image.src || $[1] !== t1) {
    t2 = <Image alt={t1} className="h-full w-full object-cover" height={128} src={image.src} width={196} />;
    $[0] = image.src;
    $[1] = t1;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  return t2;
}
export function BaseCartLine(t0) {
  const $ = _c(92);
  const {
    baseCartLineMasked,
    cartMasked,
    className,
    dictionaryPromise,
    lang
  } = t0;
  let t1;
  if ($[0] !== dictionaryPromise || $[1] !== lang) {
    t1 = {
      dictionaryPromise,
      lang
    };
    $[0] = dictionaryPromise;
    $[1] = lang;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const intl = useGetIntl(t1);
  let baseCartLineFragment;
  let cart;
  let editItemQuantityButtonclassName;
  let merchandise;
  let merchandiseUrl;
  let preOrder;
  let product;
  let t2;
  if ($[3] !== baseCartLineMasked || $[4] !== cartMasked) {
    const merchandiseSearchParams = new URLSearchParams();
    baseCartLineFragment = readFragmentShopifyStorefront(BaseCartLine_BaseCartLine_Fragment, baseCartLineMasked);
    ({
      merchandise
    } = baseCartLineFragment);
    merchandise.selectedOptions.forEach(t3 => {
      const {
        name,
        value
      } = t3;
      if (value !== DEFAULT_OPTION) {
        merchandiseSearchParams.set(name.toLowerCase(), value);
      }
    });
    let t4;
    if ($[13] !== cartMasked) {
      t4 = readFragmentShopifyStorefront(Cart_Fragment, cartMasked);
      $[13] = cartMasked;
      $[14] = t4;
    } else {
      t4 = $[14];
    }
    cart = t4;
    const {
      imageMasked,
      productMasked
    } = merchandise;
    product = readFragmentShopifyStorefront(BaseCartLine_Product_Fragment, productMasked);
    const {
      handle,
      sellingPlanGroupsConnectionMasked
    } = product;
    const sellingPlanGroups = sellingPlanGroupsConnectionMasked.edges.map(_temp);
    merchandiseUrl = `/products/${handle}?${merchandiseSearchParams.toString()}`;
    let t5;
    if ($[15] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = cn("ease flex h-full min-w-[36px] max-w-[36px] flex-none items-center justify-center rounded-full px-2 transition-all duration-200 hover:border-neutral-800 hover:opacity-80");
      $[15] = t5;
    } else {
      t5 = $[15];
    }
    editItemQuantityButtonclassName = t5;
    preOrder = sellingPlanGroups.find(_temp2);
    const imageShopifyFormat = readFragmentShopifyStorefront(Image_Fragment, imageMasked);
    t2 = imageShopifyFormat ? transformShopifyImageToNextImage(imageShopifyFormat) : null;
    $[3] = baseCartLineMasked;
    $[4] = cartMasked;
    $[5] = baseCartLineFragment;
    $[6] = cart;
    $[7] = editItemQuantityButtonclassName;
    $[8] = merchandise;
    $[9] = merchandiseUrl;
    $[10] = preOrder;
    $[11] = product;
    $[12] = t2;
  } else {
    baseCartLineFragment = $[5];
    cart = $[6];
    editItemQuantityButtonclassName = $[7];
    merchandise = $[8];
    merchandiseUrl = $[9];
    preOrder = $[10];
    product = $[11];
    t2 = $[12];
  }
  const image = t2;
  let t3;
  if ($[16] !== className) {
    t3 = cn("relative flex gap-4 w-full flex-row justify-between", className);
    $[16] = className;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  let t4;
  if ($[18] !== baseCartLineFragment || $[19] !== cart.id || $[20] !== dictionaryPromise || $[21] !== lang) {
    t4 = <DeleteItemButton cartId={cart.id} className="absolute -top-2 -end-2 self-start z-10 p-2" dictionaryPromise={dictionaryPromise} baseCartLineFragment={baseCartLineFragment} lang={lang} />;
    $[18] = baseCartLineFragment;
    $[19] = cart.id;
    $[20] = dictionaryPromise;
    $[21] = lang;
    $[22] = t4;
  } else {
    t4 = $[22];
  }
  let t5;
  if ($[23] !== image || $[24] !== product.title) {
    t5 = <CartLineProductImage image={image} title={product.title} />;
    $[23] = image;
    $[24] = product.title;
    $[25] = t5;
  } else {
    t5 = $[25];
  }
  let t6;
  if ($[26] !== t4 || $[27] !== t5) {
    t6 = <figure className="aspect-2/3 relative w-16 cursor-pointer rounded-md border border-neutral-300 bg-neutral-300 dark:border-neutral-700 dark:bg-neutral-900 dark:hover:bg-neutral-800 overflow-visible">{t4}{t5}</figure>;
    $[26] = t4;
    $[27] = t5;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  let t7;
  if ($[29] !== product.title) {
    t7 = <span>{product.title}</span>;
    $[29] = product.title;
    $[30] = t7;
  } else {
    t7 = $[30];
  }
  let t8;
  if ($[31] !== merchandise.title) {
    t8 = merchandise.title !== DEFAULT_OPTION ? <span className="text-xs">{merchandise.title}</span> : null;
    $[31] = merchandise.title;
    $[32] = t8;
  } else {
    t8 = $[32];
  }
  let t9;
  if ($[33] !== t7 || $[34] !== t8) {
    t9 = <div className="flex flex-col gap-2">{t7}{t8}</div>;
    $[33] = t7;
    $[34] = t8;
    $[35] = t9;
  } else {
    t9 = $[35];
  }
  let t10;
  if ($[36] !== baseCartLineFragment.cost.totalAmount.amount || $[37] !== baseCartLineFragment.cost.totalAmount.currencyCode || $[38] !== lang) {
    t10 = <Price amount={baseCartLineFragment.cost.totalAmount.amount} className="text-xs" currencyCode={baseCartLineFragment.cost.totalAmount.currencyCode} lang={lang} />;
    $[36] = baseCartLineFragment.cost.totalAmount.amount;
    $[37] = baseCartLineFragment.cost.totalAmount.currencyCode;
    $[38] = lang;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  let t11;
  if ($[40] !== t10 || $[41] !== t9) {
    t11 = <div className="flex flex-1 flex-col gap-4 text-sm justify-between">{t9}{t10}</div>;
    $[40] = t10;
    $[41] = t9;
    $[42] = t11;
  } else {
    t11 = $[42];
  }
  let t12;
  if ($[43] !== merchandiseUrl || $[44] !== t11 || $[45] !== t6) {
    t12 = <Link className="relative flex flex-row space-x-4" href={merchandiseUrl}>{t6}{t11}</Link>;
    $[43] = merchandiseUrl;
    $[44] = t11;
    $[45] = t6;
    $[46] = t12;
  } else {
    t12 = $[46];
  }
  let t13;
  if ($[47] !== intl) {
    t13 = intl.formatMessage({
      defaultMessage: "Edit item quantity",
      id: "VJz9sN",
      description: "Text for an edit item quantity button"
    });
    $[47] = intl;
    $[48] = t13;
  } else {
    t13 = $[48];
  }
  let t14;
  if ($[49] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <HiOutlineMinus className="h-4 w-4 dark:text-neutral-500" />;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  let t15;
  if ($[50] !== editItemQuantityButtonclassName || $[51] !== t13) {
    t15 = <Button aria-disabled={true} aria-label={t13} className={editItemQuantityButtonclassName} disabled={true} variant="ghost">{t14}</Button>;
    $[50] = editItemQuantityButtonclassName;
    $[51] = t13;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  let t16;
  if ($[53] !== baseCartLineFragment || $[54] !== cart.id || $[55] !== dictionaryPromise || $[56] !== editItemQuantityButtonclassName || $[57] !== lang) {
    t16 = <EditItemQuantityButton cartId={cart.id} className={editItemQuantityButtonclassName} dictionaryPromise={dictionaryPromise} baseCartLineFragment={baseCartLineFragment} lang={lang} type="minus" />;
    $[53] = baseCartLineFragment;
    $[54] = cart.id;
    $[55] = dictionaryPromise;
    $[56] = editItemQuantityButtonclassName;
    $[57] = lang;
    $[58] = t16;
  } else {
    t16 = $[58];
  }
  let t17;
  if ($[59] !== t15 || $[60] !== t16) {
    t17 = <Suspense fallback={t15}>{t16}</Suspense>;
    $[59] = t15;
    $[60] = t16;
    $[61] = t17;
  } else {
    t17 = $[61];
  }
  let t18;
  if ($[62] !== baseCartLineFragment.quantity) {
    t18 = <span className="w-6 text-center text-sm">{baseCartLineFragment.quantity}</span>;
    $[62] = baseCartLineFragment.quantity;
    $[63] = t18;
  } else {
    t18 = $[63];
  }
  let t19;
  if ($[64] !== intl) {
    t19 = intl.formatMessage({
      defaultMessage: "Edit item quantity",
      id: "VJz9sN",
      description: "Text for an edit item quantity button"
    });
    $[64] = intl;
    $[65] = t19;
  } else {
    t19 = $[65];
  }
  let t20;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <HiOutlinePlus className="h-4 w-4 dark:text-neutral-500" />;
    $[66] = t20;
  } else {
    t20 = $[66];
  }
  let t21;
  if ($[67] !== editItemQuantityButtonclassName || $[68] !== t19) {
    t21 = <Button aria-disabled={true} aria-label={t19} className={editItemQuantityButtonclassName} disabled={true} variant="ghost">{t20}</Button>;
    $[67] = editItemQuantityButtonclassName;
    $[68] = t19;
    $[69] = t21;
  } else {
    t21 = $[69];
  }
  let t22;
  if ($[70] !== baseCartLineFragment || $[71] !== cart.id || $[72] !== dictionaryPromise || $[73] !== editItemQuantityButtonclassName || $[74] !== lang) {
    t22 = <EditItemQuantityButton cartId={cart.id} className={editItemQuantityButtonclassName} dictionaryPromise={dictionaryPromise} baseCartLineFragment={baseCartLineFragment} lang={lang} type="plus" />;
    $[70] = baseCartLineFragment;
    $[71] = cart.id;
    $[72] = dictionaryPromise;
    $[73] = editItemQuantityButtonclassName;
    $[74] = lang;
    $[75] = t22;
  } else {
    t22 = $[75];
  }
  let t23;
  if ($[76] !== t21 || $[77] !== t22) {
    t23 = <Suspense fallback={t21}>{t22}</Suspense>;
    $[76] = t21;
    $[77] = t22;
    $[78] = t23;
  } else {
    t23 = $[78];
  }
  let t24;
  if ($[79] !== t17 || $[80] !== t18 || $[81] !== t23) {
    t24 = <div className="ms-auto flex h-9 flex-row items-center rounded-full border border-neutral-200 dark:border-neutral-700">{t17}{t18}{t23}</div>;
    $[79] = t17;
    $[80] = t18;
    $[81] = t23;
    $[82] = t24;
  } else {
    t24 = $[82];
  }
  let t25;
  if ($[83] !== preOrder) {
    t25 = preOrder && <span className="text-xs text-center">{preOrder.name}</span>;
    $[83] = preOrder;
    $[84] = t25;
  } else {
    t25 = $[84];
  }
  let t26;
  if ($[85] !== t24 || $[86] !== t25) {
    t26 = <div className="flex flex-col justify-between">{t24}{t25}</div>;
    $[85] = t24;
    $[86] = t25;
    $[87] = t26;
  } else {
    t26 = $[87];
  }
  let t27;
  if ($[88] !== t12 || $[89] !== t26 || $[90] !== t3) {
    t27 = <article className={t3}>{t12}{t26}</article>;
    $[88] = t12;
    $[89] = t26;
    $[90] = t3;
    $[91] = t27;
  } else {
    t27 = $[91];
  }
  return t27;
}
function _temp2(t0) {
  const {
    name: name_0
  } = t0;
  return name_0 === "Pre-order";
}
function _temp(t0) {
  const {
    node: sellingPlanGroupMasked
  } = t0;
  return readFragmentShopifyStorefront(SellingPlanGroup_Fragment, sellingPlanGroupMasked);
}